import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import { useEffect, useMemo, useState } from 'react';
import theme from '@src/theme';
import {
  addDays,
  checkSameOrAfter,
  checkSameOrBefore,
  dayAndNumber,
  dayAndNumberAndMonth,
  endOfWeek,
  getDaysBetween,
  isBetween,
  isTheSameDay,
  moveWeek,
  startOfWeek,
  subtractDays,
} from '@src/utils/dateUtils';
import CrewName from '@src/components/user/CrewName';
import CrewButton from './CrewButton';
import JobAvailabilityDTO from '@src/adapters/dto/job/JobAvailabilityDTO';
import Button from '@mui/material/Button';
import useGetAvailabilitiesForJobLazy from '@src/adapters/hooks/job/useGetAvailabilitiesForJob';
import { JobBookingStatusHubLocked } from '@src/constants/JobBookingStatus';
import useSaveJobActivityLazy from '@src/adapters/hooks/job/useSaveJobActivity';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';

interface Props {
  job: JobDTO;
}

function AssignCrew({ job }: Props) {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const { execute: getAvailabilities } = useGetAvailabilitiesForJobLazy();
  const { execute: save } = useSaveJobActivityLazy();
  const [availabilities, setAvailabilities] = useState<JobAvailabilityDTO[]>(
    []
  );
  const nextDisabled =
    !startDate ||
    !endDate ||
    !checkSameOrBefore(moveWeek(startDate, 'forward'), job.endDate!);

  const prevDisabled =
    !startDate ||
    !endDate ||
    !checkSameOrAfter(moveWeek(endDate, 'backward'), job.startDate!);

  useEffect(() => {
    if (job.id && job.startDate) {
      const sow = startOfWeek(job.startDate);
      const eow = endOfWeek(job.startDate);
      setStartDate(sow);
      setEndDate(eow);

      getAvailabilities({
        startDate: sow,
        endDate: eow,
        jobId: job.id,
      }).then((result) => {
        setAvailabilities(result.availabilities);
      });
    }
  }, [job, getAvailabilities]);

  const days = useMemo(() => {
    if (startDate && endDate) {
      return getDaysBetween(startDate, endDate);
    }
    return [];
  }, [startDate, endDate]);

  const users = useMemo(() => {
    const counts = new Map<string, number>();

    availabilities.forEach((a) => {
      counts.set(a.userId, (counts.get(a.userId) || 0) + 1);
    });

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
  }, [availabilities]);

  const handleNextClick = async () => {
    if (startDate && endDate) {
      const newStart = moveWeek(startDate, 'forward');
      const newEnd = moveWeek(endDate, 'forward');

      if (checkSameOrBefore(newStart, job.endDate!)) {
        setStartDate(newStart);
        setEndDate(newEnd);
        getAvailabilities({
          startDate: newStart,
          endDate: newEnd,
          jobId: job.id!,
        }).then((result) => {
          setAvailabilities(result.availabilities);
        });
      }
    }
  };

  const handlePreviousClick = async () => {
    if (startDate && endDate) {
      const newStart = moveWeek(startDate, 'backward');
      const newEnd = moveWeek(endDate, 'backward');
      if (checkSameOrAfter(newEnd, job.startDate!)) {
        setStartDate(newStart);
        setEndDate(newEnd);
        getAvailabilities({
          startDate: newStart,
          endDate: newEnd,
          jobId: job.id!,
        }).then((result) => {
          setAvailabilities(result.availabilities);
        });
      }
    }
  };

  const handleAvailabilityClicked = async (
    availability: JobAvailabilityDTO
  ) => {
    if (job.id && !JobBookingStatusHubLocked.includes(availability.status)) {
      const result = await save({
        availabilityId: availability.id,
        jobId: job.id,
        status: availability.status,
      });

      if (job) {
        setAvailabilities((prevAvailabilities) => {
          return prevAvailabilities.map((a) =>
            a.id === availability.id ? { ...a, status: result.status } : a
          );
        });
      }
    }
  };

  return (
    <Paper elevation={8}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            paddingBottom: theme.spacing(2),
          }}>
          Assign Crew
        </Typography>
        <Box>
          <Button
            onClick={handlePreviousClick}
            disabled={prevDisabled}
            color="secondary"
            variant="contained"
            size="medium"
            sx={(theme) => ({
              '&.Mui-disabled': {
                color: theme.palette.grey[700],
                borderColor: theme.palette.grey[800],
              },
              height: '45px',
            })}>
            <NavigateBeforeIcon />
          </Button>
          <Button
            onClick={handleNextClick}
            disabled={nextDisabled}
            color="secondary"
            variant="contained"
            size="medium"
            sx={(theme) => ({
              '&.Mui-disabled': {
                color: theme.palette.grey[700],
                borderColor: theme.palette.grey[800],
              },
              height: '45px',
            })}>
            <NavigateNextIcon />
          </Button>
        </Box>
      </Box>
      <TableContainer
        style={{
          maxHeight: '60vh',
          overflowX: 'auto',
          overflowY: 'auto',
          width: '100%',
          maxWidth: '90vw',
        }}>
        <Table
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead>
            <TableRow>
              <TableCell
                component="th"
                align="left"
                sx={{
                  width: '15%',
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  backgroundColor: '#151517',
                }}>
                Name
              </TableCell>
              {days.map((d, index) => (
                <TableCell
                  key={index}
                  component="th"
                  align="center"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    backgroundColor: '#151517',
                    width: `${85 / days.length}%`,
                  }}>
                  {dayAndNumberAndMonth(d)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u, index) => (
              <TableRow key={index}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: '#151517',
                  }}>
                  <CrewName id={u} />
                </TableCell>
                {days.map((d, index) =>
                  checkSameOrAfter(d, job.startDate!) &&
                  checkSameOrBefore(d, job.endDate!) ? (
                    <TableCell component="th" align="center" key={index}>
                      <CrewButton
                        availability={availabilities.find(
                          (a) => isTheSameDay(a.date, d) && a.userId === u
                        )}
                        availabilityClicked={handleAvailabilityClicked}
                      />
                    </TableCell>
                  ) : (
                    <TableCell key={index} align="center">
                      <Button
                        variant="outlined"
                        disabled
                        sx={(theme) => ({
                          minWidth: 0,
                          width: 40,
                          height: 40,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: 'auto',
                          color: theme.palette.grey[600],
                          borderColor: theme.palette.grey[700],
                          backgroundColor: theme.palette.grey[900],
                          '&.Mui-disabled': {
                            color: theme.palette.grey[600],
                            borderColor: theme.palette.grey[700],
                          },
                        })}>
                        <DoNotDisturbIcon />
                      </Button>
                    </TableCell>
                  )
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default AssignCrew;
